/**
 * The pure decision core of client input.
 *
 * Everything here is a plain function over plain data: no DOM, no camera, no
 * PixiJS. `inputController.ts` is the thin, untestable shell that feeds these
 * functions browser events. Anything that has to be *right* — where a gesture
 * becomes a surge, which edges of the frontier that surge covers — lives here
 * so it can be tested in milliseconds without a headless browser.
 *
 * These functions never decide an outcome. They translate a pointer path into
 * the *intent* the player expressed; the server decides what happens next.
 */

import {
  HEX_DIRECTIONS,
  NEUTRAL_LAND_CAPTURABLE_BY_PRESSURE,
  borderSegmentId,
  hexAdd,
  hexDistance,
  hexKey,
  hexLine,
  pixelToHex,
} from "kngdm-shared";
import type { BorderSegmentId, HexCoord, KingdomId, Point } from "kngdm-shared";

// ---------------------------------------------------------------------------
// Gesture classification
// ---------------------------------------------------------------------------

export type GestureKind = "click" | "drag";

export interface GestureThresholds {
  /** Movement at or beyond this is a drag regardless of how long it took. */
  readonly dragDistancePx?: number;
  /** Movement at or below this is hand-tremor, never a drag. */
  readonly clickSlopPx?: number;
  /** Between slop and drag distance, a gesture must last this long to be a drag. */
  readonly slowDragMs?: number;
}

/**
 * A decisive shove. Chosen above the couple of pixels a mouse drifts during a
 * click but below the width of a hex at default zoom, so an intentional
 * one-hex push still registers.
 */
const DEFAULT_DRAG_DISTANCE_PX = 8;

/** Below this, the pointer never left the hex the player meant to click. */
const DEFAULT_CLICK_SLOP_PX = 3;

const DEFAULT_SLOW_DRAG_MS = 180;

/**
 * Distance alone can't separate the two gestures: a fast flick across the map
 * is a real drag despite lasting 80ms, while a careful click on a trackpad can
 * wander five pixels over half a second. So distance decides the clear cases
 * and duration breaks the tie in the ambiguous band between slop and the drag
 * threshold. Without the time term, a click that wobbles by two pixels emits a
 * one-hex surge the player never asked for.
 */
export function classifyGesture(
  startPx: Point,
  endPx: Point,
  elapsedMs: number,
  opts?: GestureThresholds,
): GestureKind {
  const dragDistance = opts?.dragDistancePx ?? DEFAULT_DRAG_DISTANCE_PX;
  const clickSlop = opts?.clickSlopPx ?? DEFAULT_CLICK_SLOP_PX;
  const slowDragMs = opts?.slowDragMs ?? DEFAULT_SLOW_DRAG_MS;

  const distance = Math.hypot(endPx.x - startPx.x, endPx.y - startPx.y);

  if (distance >= dragDistance) return "drag";
  if (distance > clickSlop && elapsedMs >= slowDragMs) return "drag";
  return "click";
}

// ---------------------------------------------------------------------------
// Pointer path -> hex path
// ---------------------------------------------------------------------------

/**
 * The hexes a drag crossed, in order, contiguous and without repeats.
 *
 * `points` are **world-space** pixels (the caller converts through the camera
 * first), matching what `pixelToHex` expects.
 *
 * Pointer events are sampled, not continuous: drag fast and the browser hands
 * you positions three hexes apart. Taking those samples literally produces a
 * frontier with holes in it — the player paints a line, sees a dashed one, and
 * the hexes in the gaps are never pushed. `hexLine` fills every gap, so the
 * emitted path is always a walk of adjacent hexes.
 */
export function pointerPathToHexPath(points: readonly Point[], hexSize: number): HexCoord[] {
  if (hexSize <= 0) throw new Error(`pointerPathToHexPath hexSize must be positive, got ${hexSize}`);

  const path: HexCoord[] = [];
  let previous: HexCoord | undefined;

  for (const point of points) {
    const current = pixelToHex(point, hexSize);
    if (previous === undefined) {
      path.push(current);
      previous = current;
      continue;
    }

    const gap = hexDistance(previous, current);
    if (gap === 0) continue; // The pointer moved within one hex; nothing new.

    if (gap === 1) {
      path.push(current);
    } else {
      // Inclusive of both ends, so drop the first — it is `previous`.
      for (const bridged of hexLine(previous, current).slice(1)) {
        path.push(bridged);
      }
    }
    previous = current;
  }

  return path;
}

// ---------------------------------------------------------------------------
// Hex path -> border segments
// ---------------------------------------------------------------------------

/**
 * The border segments a painted path asks to surge.
 *
 * For every hex on the path the player owns, each of its six edges facing a hex
 * owned by a *different* kingdom becomes a segment id. Edges facing your own
 * territory are interior, not frontier. Edges facing unclaimed land are never
 * emitted: per `NEUTRAL_LAND_CAPTURABLE_BY_PRESSURE` (false) and docs/01,
 * neutral land is bought with resources via `claimHex` and enemy land is taken
 * with troops. Blurring those two paths gives troops a free expansion route,
 * makes the economy decoration, and ends every session in total conquest — that
 * is not hypothetical, it is the bug this guard exists to prevent.
 *
 * `ownedKeys` holds `hexKey` strings for the player's territory; `ownerAt`
 * reports the owner of any hex (null for unclaimed or off-map).
 */
export function surgeSegmentsForPath(
  path: readonly HexCoord[],
  ownedKeys: ReadonlySet<string>,
  ownerAt: (coord: HexCoord) => KingdomId | null,
): BorderSegmentId[] {
  // A path may re-enter a hex it already crossed; the same edge must not be
  // surged twice. Insertion order is preserved, so segments stay in paint order.
  const segments = new Set<BorderSegmentId>();

  for (const coord of path) {
    if (!ownedKeys.has(hexKey(coord))) continue;

    for (let direction = 0; direction < HEX_DIRECTIONS.length; direction++) {
      const offset = HEX_DIRECTIONS[direction];
      /* c8 ignore next */
      if (offset === undefined) continue;

      const neighbour = hexAdd(coord, offset);
      if (ownedKeys.has(hexKey(neighbour))) continue; // Interior edge.

      const neighbourOwner = ownerAt(neighbour);
      if (neighbourOwner === null && !NEUTRAL_LAND_CAPTURABLE_BY_PRESSURE) continue;

      segments.add(borderSegmentId(coord, direction));
    }
  }

  return [...segments];
}
